import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, message } from 'antd';
import { ArrowRightOutlined, FileTextOutlined } from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { financeSettingsApi } from '../../utils/api';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import InvoiceForm from '../../components/finance/InvoiceForm';
import { formatJalaliApiDate } from '../../utils/dateUtils';
import { useDetailReturnRefs } from '../../hooks/useListReturnState';
import { mapItemDiscountFields } from '../../utils/itemDiscountUtils';

const createEmptyItem = () => ({
  item_id: null,
  product_id: null,
  item_name: '',
  quantity: 1,
  unit_id: null,
  cost_per_item: 0,
  discount: 0,
  discount_type: 'percent',
  taxes: [],
  amount: 0,
  item_summary: '',
  invoice_item_image: null,
});

const EditInvoice = () => {
  const navigate = useNavigate();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [options, setOptions] = useState({});
  const [initialValues, setInitialValues] = useState({});
  const [initialItems, setInitialItems] = useState([createEmptyItem()]);

  const goBackToList = () => {
    navigate(returnToRef.current || '/finance/invoices', { state: buildGoBackState() });
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const res = await financeSettingsApi.getInvoiceEditData(id);
        const data = res?.data || {};
        const invoice = data.invoice || {};
        setOptions(data);
        setInitialValues({
          invoice_number: invoice.invoice_number,
          issue_date: formatJalaliApiDate(invoice.issue_date),
          due_date: formatJalaliApiDate(invoice.due_date),
          currency_id: invoice.currency_id,
          client_id: invoice.client_id,
          project_id: invoice.project_id || undefined,
          calculate_tax: invoice.calculate_tax || 'after_discount',
          bank_account_id: invoice.bank_account_id || undefined,
          invoice_payment_id: invoice.invoice_payment_id || undefined,
          company_address_id: invoice.company_address_id || undefined,
          category_id: invoice.category_id || undefined,
          shipping_address: invoice.shipping_address || '',
          discount_value: Number(invoice.discount || 0),
          discount_type: invoice.discount_type || 'percent',
          note: invoice.note || '',
          payment_status: String(invoice.payment_status || '0') === '1',
          gateway: invoice.gateway || 'Offline',
          offline_methods: invoice.offline_method_id || undefined,
          transaction_id: invoice.transaction_id || '',
          show_shipping_address: invoice.show_shipping_address === 'yes',
        });

        const mappedItems = (invoice.items || []).map((it) => {
          let parsedTaxes = [];
          try {
            parsedTaxes = Array.isArray(it.taxes) ? it.taxes : JSON.parse(it.taxes || '[]');
          } catch {
            parsedTaxes = [];
          }
          return {
            item_id: it.id,
            product_id: it.product_id,
            item_name: it.item_name || '',
            quantity: Number(it.quantity || 0),
            unit_id: it.unit_id,
            cost_per_item: Number(it.unit_price || 0),
            taxes: parsedTaxes.map((x) => Number(x)),
            item_summary: it.item_summary || '',
            invoice_item_image: it.invoice_item_image || null,
            ...mapItemDiscountFields(it),
          };
        });
        setInitialItems(mappedItems.length ? mappedItems : [createEmptyItem()]);
      } catch (error) {
        message.error('خطا در دریافت اطلاعات ویرایش فاکتور');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleSubmit = async (payload) => {
    setSubmitLoading(true);
    try {
      await financeSettingsApi.updateInvoice(id, payload);
      message.success('فاکتور با موفقیت ویرایش شد');
      navigate(returnToRef.current || '/finance/invoices', { state: buildGoBackState() });
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <MainLayout>
      <div style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                <FileTextOutlined className="text-blue-600 dark:text-blue-400 text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">ویرایش فاکتور</h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">فاکتور شماره {id}</p>
              </div>
            </div>
            <Button icon={<ArrowRightOutlined />} onClick={goBackToList}>
              بازگشت به لیست
            </Button>
          </div>
          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            {loading ? (
              <TableSkeleton rows={6} columns={2} />
            ) : (
              <InvoiceForm
                options={options}
                initialValues={initialValues}
                initialItems={initialItems}
                isEdit
                submitLoading={submitLoading}
                onSubmit={handleSubmit}
              />
            )}
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default EditInvoice;
